import { useStore } from "@/store";
import {
  useActivePlaylistVideos,
  useUserPlaylistData,
} from "./playlistSelectors";
import { useAdvizerMap, useIsLoggedIn, useVimeoIdVideoMap } from "../selectors";
import { IAdvizerVideo } from "@/models/AdvizerVideo";
import { PlaylistService } from "@/services/PlaylistService";
import { AssignmentService } from "@/services/AssignmentService";
import { useMemo } from "react";
import { IUserVideoReaction } from "@/types/UserDataTypes";
import { IAdvizerTag } from "@/components/AdvizerTag";
import { ResourceConfigs } from "@/configs/resourceConfigs";
import {
  IUserLevelConfig,
  levelConfigs as originalLevelConfigs,
} from "@/configs/levels";

export const useUserProfileData = () => {
  return useStore((state) => state.user?.profileData);
};

export const useUserRecommendedPlaylists = () => {
  return (
    useStore((state) => state.user?.playlistData?.recommendedPlaylists) || {}
  );
};

export const useUserCompletedPlaylistKeys = () => {
  const userPlaylistData = useUserPlaylistData();
  return PlaylistService.getUserCompletedPlaylistKeys({
    userPlaylistData,
  });
};

export const useUserInProgressPlaylistKeys = () => {
  const playlistData = useUserPlaylistData();

  return PlaylistService.getUserInProgressPlaylistKeys({
    userPlaylistData: playlistData,
  });
};

export const useUserVideoData = () => {
  return useStore((state) => state.user?.videoData?.videos) || {};
};

export const useUserSubjects = () => {
  const profileData = useUserProfileData();
  return profileData?.subjects || {};
};

export const useUserHasSelectedSubjects = () => {
  const subjects = useUserSubjects();
  return Object.keys(subjects).length > 0;
};

export const useUserIndustries = () => {
  const profileData = useUserProfileData();
  return profileData?.industries || {};
};

export const useUserHasSelectedIndustries = () => {
  const industries = useUserIndustries();
  return Object.keys(industries).length > 0;
};

export const useUserPlaylistKeys = () => {
  const playlistData = useUserPlaylistData();
  return Object.keys(playlistData?.playlists || {});
};

export const useUserVideosWatched = (): IAdvizerVideo[] => {
  const videoMap = useVimeoIdVideoMap();
  const userVideoData = useUserVideoData();
  return AssignmentService.getUserVideosWatched({
    videoMap,
    userVideoData,
  });
};

export const useUserAdvizersWatched = () => {
  const advizerMap = useAdvizerMap();
  const videosWatched = useUserVideosWatched();

  return AssignmentService.getUserAdvizersWatched({
    advizerMap,
    userVideosWatched: videosWatched,
  });
};

export const useRankedAdvizersWatchedData = () => {
  const advizerMap = useAdvizerMap();
  const userVideosWatched = useUserVideosWatched();
  const userVideoData = useUserVideoData();

  return AssignmentService.getUserRankedAdvizersWatchedData({
    advizerMap,
    userVideosWatched,
    userVideoData,
  });
};

export const useTotalSecondsWatchedOfPlaylistVideos = ({
  playlistId,
  userVideoData,
}: {
  playlistId: string;
  userVideoData: any;
}) => {
  const activePlaylistVideos = useActivePlaylistVideos(playlistId || "");

  const totalSecondsWatched = activePlaylistVideos.reduce((acc, video) => {
    if (!userVideoData?.[video.vimeoId]) {
      return acc;
    }
    const secondsWatched = Math.min(
      Math.max(
        userVideoData[video.vimeoId]?.secondsWatched || 0,
        userVideoData[video.vimeoId]?.totalSecondsWatched || 0,
      ),
      video.duration || 0,
    );
    const videoDuration = video.duration || 0;
    const percentageWatched = secondsWatched / videoDuration;
    if (percentageWatched > 0.9) {
      return acc + videoDuration;
    }
    return acc + Math.min(secondsWatched, videoDuration);
  }, 0);

  return totalSecondsWatched;
};

export const useSecondsWatchedOfVideo = ({ vimeoId }: { vimeoId: string }) => {
  const userVideoData = useUserVideoData();
  const video = userVideoData[vimeoId];
  return video?.secondsWatched || video?.totalSecondsWatched || 0;
};

export const usePercentWatchedOfVideo = ({ vimeoId }: { vimeoId: string }) => {
  const videoMap = useVimeoIdVideoMap();
  const video = videoMap[vimeoId];
  const totalSecondsWatched = useSecondsWatchedOfVideo({ vimeoId });
  const videoDuration = video?.duration || 0;
  return Math.round(
    (Math.min(totalSecondsWatched, videoDuration) / videoDuration) * 100,
  );
};

/**** Reactions **** */
export const useUserVideoReactions = (): Record<string, IUserVideoReaction> => {
  const reactions = useStore((state) => state.user?.videoReactions);
  return reactions ?? {};
};

export const useSpecificVideoReaction = ({ vimeoId }: { vimeoId: string }) => {
  const videoReactions = useUserVideoReactions();
  return videoReactions[vimeoId];
};

export const useTotalVideoReactions = () => {
  const videoReactions = useUserVideoReactions();
  return Object.keys(videoReactions).length;
};
export const useTotalPositiveVideoReactions = () => {
  const videoReactions = useUserVideoReactions();
  return Object.values(videoReactions).filter(
    (reaction) => reaction.reaction === "positive",
  ).length;
};
export const useTotalNeutralVideoReactions = () => {
  const videoReactions = useUserVideoReactions();
  return Object.values(videoReactions).filter(
    (reaction) => reaction.reaction === "neutral",
  ).length;
};
export const useTotalNegativeVideoReactions = () => {
  const videoReactions = useUserVideoReactions();
  return Object.values(videoReactions).filter(
    (reaction) => reaction.reaction === "negative",
  ).length;
};

export const useTotalSecondsWatchedOfVideoAllVideos = () => {
  const userVideoData = useUserVideoData();
  const totalSecondsWatched = useMemo(
    () =>
      Object.values(userVideoData).reduce((acc, video) => {
        return acc + (video.totalSecondsWatched || 0);
      }, 0),
    [userVideoData],
  );
  return totalSecondsWatched;
};

export const useTotalReactions = () => {
  const totalPositiveVideoReactions = useTotalPositiveVideoReactions();
  const totalNeutralVideoReactions = useTotalNeutralVideoReactions();
  const totalNegativeVideoReactions = useTotalNegativeVideoReactions();
  return (
    totalPositiveVideoReactions +
    totalNeutralVideoReactions +
    totalNegativeVideoReactions
  );
};

type ReactionsPerTag = Record<
  string,
  Record<string, Record<string, IUserVideoReaction>>
>;

export const useReactionsPerTag = (
  tagType: "jobFunctions" | "industries" | "subjects" | "traits",
): ReactionsPerTag => {
  const videoReactions = useUserVideoReactions();
  const videoMap = useVimeoIdVideoMap();

  const reactionsPerTag: ReactionsPerTag = {};
  Object.entries(videoReactions).forEach(([vimeoId, reaction]) => {
    const reactionKey = reaction.reaction;

    if (!reactionsPerTag[reactionKey]) {
      reactionsPerTag[reactionKey] = {};
    }

    const video = videoMap[vimeoId];
    if (!video) {
      // console.warn("Video not found", vimeoId);
      return;
    }

    const advizer = video.advizer;

    if (!advizer) {
      console.error("Advizer not found", vimeoId);
      return;
    }

    (advizer[tagType] || []).forEach((tag) => {
      if (!reactionsPerTag[reactionKey][tag.slug]) {
        reactionsPerTag[reactionKey][tag.slug] = {};
      }

      reactionsPerTag[reactionKey][tag.slug][vimeoId] = reaction;
    });
  });

  return reactionsPerTag;
  // console.log("videoReactions", videoReactions);
  // const jobFunctions = useJobFunctions();
};

export const useReactionTotalToTagMap = (
  tagType: "jobFunctions" | "industries" | "subjects" | "traits",
) => {
  const reactionsPerTag = useReactionsPerTag(tagType);
  const apiData = useStore((state) => state.advizeApiData[tagType]) || [];

  // Map tagType to ResourceConfig key
  const tagTypeToResourceConfigKey: Record<
    typeof tagType,
    keyof typeof ResourceConfigs
  > = {
    jobFunctions: "jobFunction",
    industries: "industry",
    subjects: "subject",
    traits: "trait",
  };
  const resourceConfigKey = tagTypeToResourceConfigKey[tagType];

  return Object.entries(reactionsPerTag).reduce(
    (acc, [reactionKey, tagMap]) => {
      if (!acc[reactionKey]) {
        acc[reactionKey] = {};
      }

      Object.entries(tagMap).forEach(([tagSlug, tagReactions]) => {
        const count = Object.keys(tagReactions).length;

        // Find the tag in API data by slug
        const tagData = apiData.find((tag: any) => tag.slug === tagSlug);

        if (tagData) {
          if (!acc[reactionKey][count]) {
            acc[reactionKey][count] = [];
          }

          acc[reactionKey][count].push({
            id: tagData.id,
            title: tagData.title,
            type: resourceConfigKey as IAdvizerTag["type"],
            slug: tagSlug,
          });
        }
      });

      return acc;
    },
    {} as Record<string, Record<number, IAdvizerTag[]>>,
  );
};

export const scoreByReaction = {
  positive: 1,
  neutral: 0,
  negative: -1,
};

export const useReactionScorePerTag = (
  tagType: "jobFunctions" | "industries" | "subjects" | "traits",
) => {
  const reactionsPerTag = useReactionsPerTag(tagType);

  // Collect all unique tag slugs across all reaction types
  const allTagSlugs = new Set<string>();
  Object.values(reactionsPerTag).forEach((tagMap) => {
    Object.keys(tagMap).forEach((tagSlug) => {
      allTagSlugs.add(tagSlug);
    });
  });

  // Calculate net score for each tag slug
  const scoreMap: Record<string, number> = {};
  allTagSlugs.forEach((tagSlug) => {
    let netScore = 0;
    Object.entries(reactionsPerTag).forEach(([reactionKey, tagMap]) => {
      const tagReactions = tagMap[tagSlug];
      if (tagReactions) {
        const count = Object.keys(tagReactions).length;
        const score =
          scoreByReaction[reactionKey as keyof typeof scoreByReaction] || 0;
        netScore += count * score;
      }
    });
    scoreMap[tagSlug] = netScore;
  });

  return { scoreMap };
};

/*** Current User Goal and Levels ***/

export const useLevelConfigs = (): IUserLevelConfig[] => {
  const levelConfigs = useMemo(
    () =>
      originalLevelConfigs.map((levelConfig, index) => {
        return {
          ...levelConfig,
          level: index + 1,
        };
      }),
    [],
  );

  return levelConfigs;
};

export const useTotalUserLevels = () => {
  const levelConfigs = useLevelConfigs();
  return levelConfigs.length;
};

export const useCurrentUserLevel = (): IUserLevelConfig | null => {
  const isLoggedIn = useIsLoggedIn();
  const userVideoData = useUserVideoData();
  const userVideosWatched = useUserVideosWatched();
  const userVideoReactions = useUserVideoReactions();
  const levelConfigs = useLevelConfigs();
  const inProgressPlaylistKeys = useUserInProgressPlaylistKeys();
  const completedPlaylistKeys = useUserCompletedPlaylistKeys();
  const totalSecondsWatched = useTotalSecondsWatchedOfVideoAllVideos();

  const checkParams = useMemo(
    () => ({
      isLoggedIn,
      userVideosWatched,
      userVideoData,
      userVideoReactions,
      inProgressPlaylistKeys,
      completedPlaylistKeys,
      totalSecondsWatched,
    }),
    [
      isLoggedIn,
      userVideosWatched,
      userVideoData,
      userVideoReactions,
      inProgressPlaylistKeys,
      completedPlaylistKeys,
      totalSecondsWatched,
    ],
  );

  const currentLevelIndex = useMemo(() => {
    return levelConfigs.findIndex(
      (levelConfig) => !levelConfig.checkComplete(checkParams),
    );
  }, [checkParams, levelConfigs]);

  const levelConfig = levelConfigs[currentLevelIndex];
  const statusString = levelConfig?.checkStatus?.(checkParams);

  const payload = useMemo(() => {
    if (!levelConfig) return null;
    return {
      ...levelConfig,
      statusString,
    };
  }, [levelConfig, statusString]);

  return payload;
};

export const useAllowAdvizerRecs = () => {
  const currentUserLevel = useCurrentUserLevel();
  const levelConfigs = useLevelConfigs();

  if (!currentUserLevel)
    return {
      allowAdvizerRecs: true,
      levelConfigRequired: null,
    };

  const levelCompletionRequired = 7;

  const levelConfigRequired = levelConfigs.find(
    (levelConfig) => levelConfig.level === levelCompletionRequired,
  );

  const allowAdvizerRecs = currentUserLevel.level > levelCompletionRequired;

  return {
    allowAdvizerRecs,
    levelConfigRequired,
  };
};

export const useAllowPlaylistRecs = () => {
  const { allowAdvizerRecs, levelConfigRequired } = useAllowAdvizerRecs();

  return {
    allowPlaylistRecs: allowAdvizerRecs,
    levelConfigRequired: levelConfigRequired,
  };
};

/*** Recommended Advizers ***/

export const useUserRecommendedAdvizers = () => {
  return (
    useStore((state) => state.user?.advizerData?.recommendedAdvizers) || {}
  );
};

export const useTotalUserRecommendedAdvizers = () => {
  const recommendedAdvizers = useUserRecommendedAdvizers();
  return Object.keys(recommendedAdvizers).length;
};

export const useUserHasRecommendedAdvizers = () => {
  const recommendedAdvizers = useUserRecommendedAdvizers();
  return Object.keys(recommendedAdvizers).length > 0;
};

export const useIsRecommendedAdvizer = ({
  advizerId,
}: {
  advizerId: string;
}) => {
  const recommendedAdvizers = useUserRecommendedAdvizers();
  return !!recommendedAdvizers[advizerId] || false;
};

export const useIsRequestingRecommendedAdvizers = () => {
  return (
    useStore((state) => state.user?.advizerData?.isRequestingAdvizerRecs) ||
    false
  );
};

export const useIsRequestingRecommendedPlaylists = () => {
  return (
    useStore((state) => state.user?.playlistData?.isRequestingPlaylistRecs) ||
    false
  );
};
